import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {
    StateOfChargeMeasurementPoint,
    VehicleActivityTypeHelper,
    VehicleBlockActivityInfo,
    VehicleWarning
} from '@modules/shared/models';
import { WarningMessageHelper } from '@modules/shared/helpers/warning-message-helper';
import { TranslateService } from '@modules/core/services/translate.service';

export interface VehicleTooltipData {
    inventoryNumber: string;
    deviationText?: string;
    deviationMinutes?: number;
    deviationColor?: string;
    isElectric?: boolean;
    stateOfCharge?: StateOfChargeMeasurementPoint;
    currentActivity?: VehicleBlockActivityInfo;
    nextActivity?: VehicleBlockActivityInfo;
    activitiesFailed?: boolean;
    activitiesLoading?: boolean;
    warnings?: VehicleWarning[];
}

@Component({
    selector: 'app-vehicle-tooltip',
    templateUrl: './vehicle-tooltip.html',
    styleUrls: ['./vehicle-tooltip.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslateModule],
    host: {
        '(mouseenter)': 'tooltipEnter.emit()',
        '(mouseleave)': 'tooltipLeave.emit()',
    }
})
export class VehicleTooltipComponent {
    // ─── Inputs & Outputs ─────────────────────────────────────────────────────

    readonly data = input<VehicleTooltipData | null>(null);
    readonly tooltipEnter = output<void>();
    readonly tooltipLeave = output<void>();

    // ─── Dependency injection ─────────────────────────────────────────────────

    private readonly _warningMessageHelper = inject(WarningMessageHelper);
    private readonly _translateService = inject(TranslateService);

    // ─── Derived state ────────────────────────────────────────────────────────

    readonly batteryDiffMinutes = computed<number | null>(() => {
        const soc = this.data()?.stateOfCharge;
        if (!soc?.measuredAt) return null;
        return (Date.now() - new Date(soc.measuredAt).getTime()) / 1000 / 60;
    });

    readonly batteryTitle = computed(() => {
        const diff = this.batteryDiffMinutes();
        if (diff === null) {
            return this._translateService.instant('Could not find any state of charge history for this vehicle');
        }
        if (diff >= 120) return this._translateService.instant('Battery status is older than 2 hours');
        if (diff >= 10) return this._translateService.instant('Battery status is older than 10 minutes');
        return '';
    });

    readonly showBatteryQuestionMark = computed(() => {
        const diff = this.batteryDiffMinutes();
        if (diff === null) return true;
        return diff >= 120 || !this.data()?.stateOfCharge?.sensorValue;
    });

    readonly batteryIconClass = computed(() => {
        const soc = this.data()?.stateOfCharge;
        const diff = this.batteryDiffMinutes();
        if (!soc?.sensorValue || diff === null) return '';
        return this._getBatteryIconClass(soc.sensorValue, diff);
    });

    // ─── Public methods ───────────────────────────────────────────────────────

    getActivityColor(type: string): string {
        return VehicleActivityTypeHelper.getColor(type as any);
    }

    getWarningIconClass(messageType: number): string {
        switch (messageType) {
            case 1: return 'fa fa-id-badge';
            case 2: return 'fa fa-battery-quarter';
            case 3: return 'far fa-clock';
            case 5: return 'fab fa-product-hunt';
            case 7: return 'fas fa-ban';
            case 8: return 'fas fa-route';
            case 9: return 'fa-solid fa-business-time';
            default: return 'fa fa-exclamation-triangle';
        }
    }

    getWarningMessage(warning: VehicleWarning): string {
        return this._warningMessageHelper.getMessage(warning);
    }

    // ─── Private methods ──────────────────────────────────────────────────────

    private _getBatteryIconClass(sensorValue: number, diffMinutes: number): string {
        let icon: string;
        if (sensorValue >= 100) icon = 'fa-battery-full';
        else if (sensorValue >= 75) icon = 'fa-battery-three-quarters';
        else if (sensorValue >= 50) icon = 'fa-battery-half';
        else if (sensorValue >= 10) icon = 'fa-battery-quarter';
        else icon = 'fa-battery-empty';
        if (diffMinutes >= 10) icon += ' grey-icon';
        return `icon fas ${icon}`;
    }
}
